
import { Ellipsis, ExternalLink, Link2 } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { CardResourceStack, ResourceMenuButton, TypographyResourceLabel } from "./CardResourceStack.styles";
import type { CardTabsLinkProps } from "./CardTabsLink.types";
import { ResourceDescriptionModal } from "./ResourceDescriptionModal.component";
import { TruncatedText } from "../../components/TruncatedText/TruncatedText.component";

export function CardTabsLink({
  description,
  disabled,
  showDescription = false,
  url_label,
  url,
  ...props
}: CardTabsLinkProps) {
  const { t } = useTranslation();
  const modalId = useId();
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const hasDescription = showDescription && Boolean(description?.trim());

  const handleLinkClick = () => {
    if (!disabled && url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <>
      <CardResourceStack
        tabIndex={ 0 }
        aria-label={ t("go_to_link",{ link:url_label }) }
        { ...props }
        onClick={ handleLinkClick }
        $disabled={ disabled }
      >
        <Link2 size={ "1rem" } />
        <TruncatedText 
          text={ url_label }
          webkitLineClamp={ 1 }
        >
          <TypographyResourceLabel>
            { url_label }
          </TypographyResourceLabel>    
        </TruncatedText>
        { hasDescription && (
          <ResourceMenuButton
            type="button"
            aria-label={ t("resource_description", { name: url_label }) }
            aria-haspopup="dialog"
            aria-expanded={ descriptionOpen }
            aria-controls={ descriptionOpen ? modalId : undefined }
            onClick={ (event) => {
              event.stopPropagation();
              setDescriptionOpen(true);
            } }
          >
            <Ellipsis size={ "1rem" } aria-hidden="true" />
          </ResourceMenuButton>
        ) }
      </CardResourceStack>
      { hasDescription && (
        <ResourceDescriptionModal
          id={ modalId }
          open={ descriptionOpen }
          icon={ <Link2 size={ "1rem" } aria-hidden="true" /> }
          title={ url_label }
          description={ description }
          actionLabel={ t("open_link") }
          actionIcon={ <ExternalLink size={ "1rem" } aria-hidden="true" /> }
          actionDisabled={ disabled || !url }
          onAction={ () => {
            handleLinkClick();
            setDescriptionOpen(false);
          } }
          onClose={ () => setDescriptionOpen(false) }
        />
      ) }
    </>
  );
}
